<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import clubAccessService from '../services/club-access.service'

const { t, locale } = useI18n()

const props = defineProps<{
  accessTo: string | null
  type?: 'FREE' | 'PAID'
}>()

const daysRemaining = computed(() => {
  if (!props.accessTo) return null
  return clubAccessService.getDaysRemaining(props.accessTo)
})

const isExpired = computed(() => {
  if (!props.accessTo) return false
  return clubAccessService.isExpired(props.accessTo)
})

const isExpiringSoon = computed(() => {
  if (!props.accessTo) return false
  return clubAccessService.isExpiringSoon(props.accessTo)
})

const badgeClasses = computed(() => {
  if (isExpired.value) {
    return 'bg-red-100 text-red-800 border-red-300'
  } else if (isExpiringSoon.value) {
    return 'bg-yellow-100 text-yellow-800 border-yellow-300'
  } else {
    return 'bg-green-100 text-green-800 border-green-300'
  }
})

const badgeText = computed(() => {
  if (!props.accessTo) {
    return t('access.no_access')
  }

  if (isExpired.value) {
    return t('access.expired')
  }

  if (isExpiringSoon.value) {
    return t('access.expires_in_days', { days: daysRemaining.value })
  }

  return t('access.active_until', { date: formatDate(props.accessTo) })
})

const badgeIcon = computed(() => {
  if (isExpired.value) {
    return 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' // X circle
  } else if (isExpiringSoon.value) {
    return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' // Warning
  } else {
    return 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' // Check circle
  }
})

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString(locale.value, { year: 'numeric', month: 'long', day: 'numeric' })
}
</script>

<template>
  <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium" :class="badgeClasses">
    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="badgeIcon" />
    </svg>
    <span>{{ badgeText }}</span>
    <span v-if="type === 'FREE'" class="text-xs opacity-75">({{ t('access.trial') }})</span>
  </div>
</template>
